import { AxiosError } from 'axios';
import jwtDecode from 'jwt-decode';
import { put, call, takeLatest, select } from 'redux-saga/effects';

import { getErrorCode, getErrorMessage } from 'services/api/normalizer';
import { Kiosk, actions as kioskActions, selectors as kiosksSelectors } from 'services/kiosk';
import i18n from 'services/translation/i18n';

import { API, HardwareAPI } from '../api/base';
import { types as kioskTypes } from '../kiosk';
import {
  types,
  AdminLoginRequest,
  AdminPinValidationRequest,
  MaintenanceLoginRequest,
  DecodedJWT,
  DemoLoginRequest
} from './types';

export function* adminLoginAsync({ payload }: AdminLoginRequest) {
  const { email, password } = payload;
  yield put({ type: types.ADMIN_LOGIN_ASYNC.PENDING, payload: true });

  try {
    // First get the system's MAC address using the local server,
    // then get the kiosk from the API using that MAC address
    let macAddress = '';
    let ipAddress = '0.0.0.0';
    const hardwareAPI = new HardwareAPI('kiosk/network');
    const { message } = yield call(hardwareAPI.getAll);
    macAddress = message.macaddress;
    ipAddress = message.ipaddress;

    const admin = new API('login');
    const { token } = yield call(admin.create, { macAddress, email, password });
    localStorage.setItem('token', token);
    const decodedToken = jwtDecode<DecodedJWT>(token);

    const kioskAPI = new API('kiosks');
    const kiosks: Kiosk[] = yield call(kioskAPI.getAll, { macAddress });

    if (!kiosks.length) {
      throw new Error('Kiosk has not been registered in our system');
    }

    const kiosk: Kiosk = yield call(kioskAPI.getOne, kiosks[0].id);
    yield put({ type: kioskTypes.GET_KIOSK_ASYNC.SUCCESS, payload: kiosk });
    yield put({ type: types.ADMIN_LOGIN_ASYNC.SUCCESS, payload: decodedToken });

    // Update the Kioks's IP address
    const updateKioskPayload: Partial<Kiosk> & { id: string } = { id: kiosk.id, ipAddress };
    yield call(kioskAPI.update, updateKioskPayload);
  } catch (err) {
    const code = getErrorCode(err as AxiosError);
    const message = getErrorMessage(err as AxiosError);

    if (code === 400 || code === 401) {
      yield put({ type: types.ADMIN_LOGIN_ASYNC.ERROR, payload: 'Invalid email and/or password' });
    } else if (code === 404) {
      yield put({ type: types.ADMIN_LOGIN_ASYNC.ERROR, payload: 'Kiosk has not been registered in our system' });
    } else if (code === 409) {
      yield put({ type: types.ADMIN_LOGIN_ASYNC.ERROR, payload: 'Duplicate kiosks with this MAC address' });
    } else if ((code as number) >= 500) {
      yield put({ type: types.ADMIN_LOGIN_ASYNC.ERROR, payload: 'Internet is down. Try again later.' });
    } else {
      yield put({ type: types.ADMIN_LOGIN_ASYNC.ERROR, payload: message });
    }
  } finally {
    yield put({ type: types.ADMIN_LOGIN_ASYNC.PENDING, payload: false });
  }
}

export function* maintenanceLoginAsync({ payload }: MaintenanceLoginRequest) {
  const { pinCode } = payload;
  yield put({ type: types.MAINTENANCE_LOGIN_ASYNC.PENDING, payload: true });

  try {
    const kiosk: Kiosk = yield select(kiosksSelectors.selectKiosk);

    const maintenanceAdmin = new API('maintenanceLogin');
    const { token } = yield call(maintenanceAdmin.create, { kioskId: kiosk.id, pinCode });
    localStorage.setItem('maintenance_token', token);
    const decodedToken = jwtDecode<DecodedJWT>(token);

    // Retrieve the IP and MAC address of the PC
    const hardwareAPI = new HardwareAPI('kiosk/network');
    const { message } = yield call(hardwareAPI.getAll);
    const macAddress = message.macaddress;
    const ipAddress = message.ipaddress;
    // Update the kiosk record if they have changed
    if (kiosk.ipAddress !== ipAddress || kiosk.macAddress !== macAddress) {
      yield put(kioskActions.updateKiosk({ ipAddress, macAddress }));
    }

    yield put({ type: types.MAINTENANCE_LOGIN_ASYNC.SUCCESS, payload: decodedToken });
  } catch (err) {
    const code = getErrorCode(err as AxiosError);
    const message = getErrorMessage(err as AxiosError);

    if (code === 400 || code === 401) {
      yield put({ type: types.MAINTENANCE_LOGIN_ASYNC.ERROR, payload: 'Invalid pin' });
    } else if ((code as number) >= 500) {
      yield put({ type: types.MAINTENANCE_LOGIN_ASYNC.ERROR, payload: 'Internet is down. Try again later.' });
    } else {
      yield put({ type: types.MAINTENANCE_LOGIN_ASYNC.ERROR, payload: message });
    }
  } finally {
    yield put({ type: types.MAINTENANCE_LOGIN_ASYNC.PENDING, payload: false });
  }
}

export function* demoLoginAsync({ payload }: DemoLoginRequest) {
  yield put({ type: types.DEMO_LOGIN_ASYNC.PENDING, payload: true });

  try {
    const demoLoginAPI = new API('demoLogin');
    const { token } = yield call(demoLoginAPI.create, payload);
    sessionStorage.removeItem('token');
    localStorage.setItem('token', token);

    const kioskAPI = new API('kiosks');
    const kiosk: Kiosk = yield call(kioskAPI.getOne, payload.kioskId);
    const decodedToken = jwtDecode<DecodedJWT>(token);
    yield put({ type: kioskTypes.GET_KIOSK_ASYNC.SUCCESS, payload: kiosk });
    yield put({ type: types.DEMO_LOGIN_ASYNC.SUCCESS, payload: decodedToken });
  } catch (err) {
    const code = getErrorCode(err as AxiosError);
    const message = getErrorMessage(err as AxiosError);

    if (code === 400 || code === 401) {
      yield put({ type: types.DEMO_LOGIN_ASYNC.ERROR, payload: 'Invalid credentials' });
    } else if (code === 404) {
      yield put({ type: types.DEMO_LOGIN_ASYNC.ERROR, payload: 'Kiosk has not been registered in our system' });
    } else if ((code as number) >= 500) {
      yield put({ type: types.DEMO_LOGIN_ASYNC.ERROR, payload: 'Internet is down. Try again later.' });
    } else {
      yield put({ type: types.DEMO_LOGIN_ASYNC.ERROR, payload: message });
    }
  } finally {
    yield put({ type: types.DEMO_LOGIN_ASYNC.PENDING, payload: false });
  }
}

export function* adminPinValidationAsync({ payload }: AdminPinValidationRequest) {
  const { pinCode } = payload;
  yield put({ type: types.ADMIN_PIN_VALIDATION_ASYNC.PENDING, payload: true });

  try {
    const kiosk: Kiosk = yield select(kiosksSelectors.selectKiosk);

    const adminValidationAPI = new API('adminValidation');
    yield call(adminValidationAPI.create, { kioskId: kiosk.id, pinCode });

    yield put({ type: types.ADMIN_PIN_VALIDATION_ASYNC.SUCCESS });
  } catch (err) {
    yield put({ type: types.ADMIN_PIN_VALIDATION_ASYNC.ERROR, payload: i18n.t('sagaError.invalidPin') });
  } finally {
    yield put({ type: types.ADMIN_PIN_VALIDATION_ASYNC.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.ADMIN_LOGIN_ASYNC.REQUEST, adminLoginAsync),
  takeLatest(types.MAINTENANCE_LOGIN_ASYNC.REQUEST, maintenanceLoginAsync),
  takeLatest(types.DEMO_LOGIN_ASYNC.REQUEST, demoLoginAsync),
  takeLatest(types.ADMIN_PIN_VALIDATION_ASYNC.REQUEST, adminPinValidationAsync)
];
